import { format } from 'date-fns';
import { CalendarIcon, RefreshCw, StickyNote, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/hooks/useApi';
import { useLogCasePhoneCall } from '@/hooks/useLogCasePhoneCall';
import { useLogDisputePhoneCall } from '@/hooks/useLogDisputePhoneCall';
import { useLogPromisePhoneCall } from '@/hooks/useLogPromisePhoneCall';
import { cn, formatDate } from '@/lib/utils';

// Form value types for modal callbacks
export interface RecordPromiseFormValues {
  promiseDate: Date | undefined;
  amount: number;
  method: string;
  notes: string;
  sendConfirmation: boolean;
}

export interface MarkPromiseFulfilledFormValues {
  paymentDate: Date;
  amountPaid: number;
  paymentMethod: string;
  reference: string;
  notes: string;
  promiseFulfilled: boolean;
}

export interface MarkPromiseBrokenFormValues {
  reason: string;
  notes: string;
  escalate: boolean;
  scheduleFollowUp: boolean;
  followUpDate: Date | undefined;
  promiseBroken: boolean;
}

interface LogCallModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly customerPhone?: string;
  readonly promiseId?: string;
  readonly onSuccess?: () => void;
  readonly disputeId?: string;
  readonly caseId?: string;
}

export function LogCallModal({
  open,
  onOpenChange,
  customerName,
  customerPhone,
  promiseId,
  onSuccess,
  disputeId,
  caseId,
}: LogCallModalProps) {
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState<Date>();
  const [followUpPickerOpen, setFollowUpPickerOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const logPromisePhoneCallMutation = useLogPromisePhoneCall();
  const logDisputePhoneCallMutation = useLogDisputePhoneCall();
  const logCasePhoneCallMutation = useLogCasePhoneCall();

  const isPending =
    logPromisePhoneCallMutation.isPending ||
    logDisputePhoneCallMutation.isPending ||
    logCasePhoneCallMutation.isPending;

  // Initialize phone number when modal opens or customerPhone changes
  useEffect(() => {
    if (open) {
      setPhoneNumber(customerPhone || '');
    }
  }, [open, customerPhone]);

  const handleSave = () => {
    const callbackOptions = {
      onSuccess: () => {
        toast.success('Call logged successfully');
        onSuccess?.();
        onOpenChange(false);
        setDuration('');
        setNotes('');
        setFollowUpDate(undefined);
        setPhoneNumber('');
      },
      onError: (error: Error) => {
        const message =
          error instanceof ApiError && (error.response as Record<string, string> | undefined)?.message
            ? (error.response as Record<string, string>).message
            : error.message || 'Failed to log call';
        toast.error(message);
      },
    };

    if (caseId) {
      logCasePhoneCallMutation.mutate(
        {
          caseId,
          phoneNumber: phoneNumber || undefined,
          durationMinutes: duration ? parseInt(duration, 10) : undefined,
          notes: notes || undefined,
          followUpDate: followUpDate ? format(followUpDate, 'yyyy-MM-dd') : undefined,
        },
        callbackOptions,
      );
    } else if (disputeId) {
      logDisputePhoneCallMutation.mutate(
        {
          disputeId,
          phoneNumber: phoneNumber || undefined,
          durationMinutes: duration ? parseInt(duration, 10) : undefined,
          notes: notes || undefined,
          followUpDate: followUpDate ? format(followUpDate, 'yyyy-MM-dd') : undefined,
        },
        callbackOptions,
      );
    } else if (promiseId) {
      logPromisePhoneCallMutation.mutate(
        {
          promiseId,
          phoneNumber: phoneNumber || undefined,
          durationMinutes: duration ? parseInt(duration, 10) : undefined,
          notes: notes || undefined,
          followUpDate: followUpDate ? format(followUpDate, 'yyyy-MM-dd') : undefined,
        },
        callbackOptions,
      );
    } else {
      toast.success('Call logged successfully');
      onSuccess?.();
      onOpenChange(false);
      setDuration('');
      setNotes('');
      setFollowUpDate(undefined);
      setPhoneNumber('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Log Call</DialogTitle>
          <DialogDescription>Record details of your call with {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer</Label>
            <Input id="customer" value={customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="duration">Duration (minutes)</Label>
            <Input
              id="duration"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="5"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter call notes..."
              rows={4}
            />
          </div>
          <div className="grid gap-2">
            <Label>Follow-up Date (Optional)</Label>
            <Popover open={followUpPickerOpen} onOpenChange={setFollowUpPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn('justify-start text-left font-normal', !followUpDate && 'text-muted-foreground')}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {followUpDate ? formatDate(followUpDate) : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={followUpDate}
                  onSelect={(date) => {
                    setFollowUpDate(date);
                    setFollowUpPickerOpen(false);
                  }}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? 'Saving...' : 'Save Call Log'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RecordPromiseModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly caseId?: string;
  readonly caseAmount?: number;
  readonly onSave?: (data: RecordPromiseFormValues) => void;
}

export function RecordPromiseModal({
  open,
  onOpenChange,
  customerName,
  caseId,
  caseAmount,
  onSave,
}: RecordPromiseModalProps) {
  const [promiseDate, setPromiseDate] = useState<Date>();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState(true);

  // Set default amount when modal opens or caseAmount changes
  useEffect(() => {
    if (caseAmount !== undefined && open) {
      setAmount(caseAmount.toString());
    }
  }, [caseAmount, open]);

  const handleSave = () => {
    const data = {
      promiseDate,
      amount: parseFloat(amount),
      method,
      notes,
      sendConfirmation,
    };

    if (onSave) {
      onSave(data);
    }

    toast.success('Promise to pay recorded successfully');
    onOpenChange(false);
    // Reset form
    setPromiseDate(undefined);
    setAmount('');
    setMethod('');
    setNotes('');
    setSendConfirmation(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Promise to Pay</DialogTitle>
          <DialogDescription>Record a payment commitment from {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer-promise">Customer</Label>
            <Input id="customer-promise" value={caseId ? `${customerName} (${caseId})` : customerName} disabled />
          </div>
          <div className="grid gap-2">
            <Label>Promise Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn('justify-start text-left font-normal', !promiseDate && 'text-muted-foreground')}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {promiseDate ? formatDate(promiseDate) : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={promiseDate} onSelect={setPromiseDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="amount">Promised Amount</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="method">Confirmation Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger id="method">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone Call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="portal">Customer Portal</SelectItem>
                <SelectItem value="in-person">In Person</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes-promise">Notes</Label>
            <Textarea
              id="notes-promise"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-confirmation"
              checked={sendConfirmation}
              onCheckedChange={(checked) => setSendConfirmation(checked as boolean)}
            />
            <Label htmlFor="send-confirmation" className="text-sm font-normal">
              Send confirmation email to customer
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Promise</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MarkPromiseFulfilledModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly promiseAmount: number;
  readonly promiseDate: Date;
  readonly onSave?: (data: MarkPromiseFulfilledFormValues) => void;
}

export function MarkPromiseFulfilledModal({
  open,
  onOpenChange,
  customerName,
  promiseAmount,
  promiseDate,
  onSave,
}: MarkPromiseFulfilledModalProps) {
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [amountPaid, setAmountPaid] = useState(promiseAmount.toString());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const handleSave = () => {
    const data = {
      paymentDate,
      amountPaid: parseFloat(amountPaid),
      paymentMethod,
      reference,
      notes,
      promiseFulfilled: true,
    };

    if (onSave) {
      onSave(data);
    }

    toast.success('Promise marked as fulfilled');
    onOpenChange(false);
    // Reset form
    setPaymentDate(new Date());
    setAmountPaid(promiseAmount.toString());
    setPaymentMethod('');
    setReference('');
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark Promise as Fulfilled</DialogTitle>
          <DialogDescription>Record payment received from {customerName}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Promise Details</Label>
            <div className="text-sm text-muted-foreground">
              <div>Promised Amount: ${promiseAmount.toFixed(2)}</div>
              <div>Promise Date: {formatDate(promiseDate)}</div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Payment Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn('justify-start text-left font-normal')}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDate(paymentDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={paymentDate}
                  onSelect={(date) => date && setPaymentDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="amount-paid">Amount Paid</Label>
            <Input
              id="amount-paid"
              type="number"
              value={amountPaid}
              onChange={(e) => setAmountPaid(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-method">Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger id="payment-method">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank-transfer">Bank Transfer</SelectItem>
                <SelectItem value="credit-card">Credit Card</SelectItem>
                <SelectItem value="debit-card">Debit Card</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reference">Payment Reference</Label>
            <Input
              id="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Transaction ID, check number, etc."
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes-fulfilled">Notes</Label>
            <Textarea
              id="notes-fulfilled"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Confirm Payment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MarkPromiseBrokenModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customerName: string;
  readonly promiseAmount: number;
  readonly promiseDate: Date;
  readonly onSave?: (data: MarkPromiseBrokenFormValues) => void;
}

export function MarkPromiseBrokenModal({
  open,
  onOpenChange,
  customerName,
  promiseAmount,
  promiseDate,
  onSave,
}: MarkPromiseBrokenModalProps) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [escalate, setEscalate] = useState(false);
  const [scheduleFollowUp, setScheduleFollowUp] = useState(true);
  const [followUpDate, setFollowUpDate] = useState<Date>();

  const handleSave = () => {
    const data = {
      reason,
      notes,
      escalate,
      scheduleFollowUp,
      followUpDate,
      promiseBroken: true,
    };

    if (onSave) {
      onSave(data);
    }

    toast.warning('Promise marked as broken');
    onOpenChange(false);
    // Reset form
    setReason('');
    setNotes('');
    setEscalate(false);
    setScheduleFollowUp(true);
    setFollowUpDate(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark Promise as Broken</DialogTitle>
          <DialogDescription>Record that {customerName} did not fulfill their payment commitment</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Promise Details</Label>
            <div className="text-sm text-muted-foreground">
              <div>Promised Amount: ${promiseAmount.toFixed(2)}</div>
              <div>Promise Date: {formatDate(promiseDate)}</div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="reason">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-contact">Unable to Contact</SelectItem>
                <SelectItem value="no-payment">No Payment Received</SelectItem>
                <SelectItem value="partial-payment">Partial Payment Only</SelectItem>
                <SelectItem value="customer-dispute">Customer Disputes Amount</SelectItem>
                <SelectItem value="financial-difficulty">Financial Difficulty</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes-broken">Notes</Label>
            <Textarea
              id="notes-broken"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details about why the promise was broken..."
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="escalate" checked={escalate} onCheckedChange={(checked) => setEscalate(checked as boolean)} />
            <Label htmlFor="escalate" className="text-sm font-normal">
              Escalate case to supervisor
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="schedule-follow-up"
              checked={scheduleFollowUp}
              onCheckedChange={(checked) => setScheduleFollowUp(checked as boolean)}
            />
            <Label htmlFor="schedule-follow-up" className="text-sm font-normal">
              Schedule follow-up action
            </Label>
          </div>
          {scheduleFollowUp && (
            <div className="grid gap-2">
              <Label>Follow-up Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('justify-start text-left font-normal', !followUpDate && 'text-muted-foreground')}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {followUpDate ? formatDate(followUpDate) : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={followUpDate} onSelect={setFollowUpDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleSave}>
            Mark as Broken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type EntityType = 'promise' | 'case';

interface ReassignUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onSave?: (userId: string, notes: string) => void;
  users?: { id: string; name: string; avatar: string }[];
  isLoadingUsers?: boolean;
  entityType?: EntityType;
}

export function ReassignUserModal({
  open,
  onOpenChange,
  selectedCount,
  onSave,
  users = [],
  isLoadingUsers = false,
  entityType = 'promise',
}: Readonly<ReassignUserModalProps>) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [notes, setNotes] = useState('');
  const entityLabel = entityType === 'case' ? 'case' : 'promise';

  const handleSave = () => {
    if (!selectedUserId) {
      toast.error('Please select a user to reassign to');
      return;
    }

    if (onSave) {
      onSave(selectedUserId, notes);
    }

    onOpenChange(false);
    // Reset form
    setSelectedUserId('');
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Reassign User
          </DialogTitle>
          <DialogDescription>
            Reassign {selectedCount} selected {entityLabel}
            {selectedCount > 1 ? 's' : ''} to another user
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="user">Select User</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={isLoadingUsers}>
              <SelectTrigger id="user">
                <SelectValue placeholder={isLoadingUsers ? 'Loading users...' : 'Choose a user'} />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={user.avatar} alt={user.name} />
                        <AvatarFallback>
                          {user.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </AvatarFallback>
                      </Avatar>
                      <span>{user.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="reassign-notes">Notes (Optional)</Label>
            <Textarea
              id="reassign-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this reassignment..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PROMISE_STATUSES = [
  { value: 'Active', label: 'Active' },
  { value: 'Broken', label: 'Broken' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Partially Paid', label: 'Partially Paid' },
  { value: 'Closed', label: 'Closed' },
];

const CASE_STATUSES = [
  { value: 'Active', label: 'Active' },
  { value: 'Promised', label: 'Promised' },
  { value: 'Escalated', label: 'Escalated' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Closed', label: 'Closed' },
];

interface UpdateStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onSave?: (status: string, notes: string) => void;
  entityType?: EntityType;
  availableStatuses?: { value: string; label: string }[];
}

interface AddNoteModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly disputeId: string;
  readonly disputeLabel: string;
  readonly customerName: string;
  readonly onSubmit: (content: string) => Promise<void>;
  readonly isPending?: boolean;
}

export function AddNoteModal({
  open,
  onOpenChange,
  disputeId,
  disputeLabel,
  customerName,
  onSubmit,
  isPending = false,
}: AddNoteModalProps) {
  const [noteText, setNoteText] = useState('');

  const handleSave = async () => {
    if (!noteText.trim()) return;
    try {
      await onSubmit(noteText.trim());
      setNoteText('');
      onOpenChange(false);
    } catch {
      // Error handling is done by the caller
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setNoteText('');
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-primary" />
            Add Note
          </DialogTitle>
          <DialogDescription>
            Add a note to dispute <span className="font-semibold">{disputeLabel}</span> — {customerName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Textarea
            placeholder="Enter your note..."
            rows={5}
            className="resize-none"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setNoteText('');
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!noteText.trim() || isPending} className="gap-2">
            <StickyNote className="h-4 w-4" />
            {isPending ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UpdateStatusModal({
  open,
  onOpenChange,
  selectedCount,
  onSave,
  entityType = 'promise',
  availableStatuses,
}: Readonly<UpdateStatusModalProps>) {
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const entityLabel = entityType === 'case' ? 'case' : 'promise';

  const statuses = availableStatuses ?? (entityType === 'case' ? CASE_STATUSES : PROMISE_STATUSES);

  const handleSave = () => {
    if (!status) {
      toast.error('Please select a status');
      return;
    }

    if (onSave) {
      onSave(status, notes);
    }

    onOpenChange(false);
    // Reset form
    setStatus('');
    setNotes('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Update Status
          </DialogTitle>
          <DialogDescription>
            Update the status for {selectedCount} selected {entityLabel}
            {selectedCount > 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="status">New Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="status-notes">Notes (Optional)</Label>
            <Textarea
              id="status-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this status change..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Update Status</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
